<template>
  <div :class="`SubVerticalMenu ${toggleOpen ? 'open' : ''}`">
    <div ref="start" class="SubVerticalMenu-start">
      <div v-for="item in items" :key="item.label">
        <!-- Back to list menu entries -->
        <div v-if="item.type == 'BACK_TO_PREVIOUS_SCREEN'" class="SubVerticalMenu-back-to-previous">
          <div @click="$emit('back-to-previous')">
            <b-icon :size="toggleOpen ? 'is-normal' : 'is-large'" icon="caret-left" />
            <span v-if="toggleOpen">
              {{ previousScreenName }}
            </span>
          </div>
        </div>

        <!-- Items without links, hidden when menu is collapsed -->
        <div v-else-if="item.type == 'NO_LINK' && toggleOpen" class="SubVerticalMenu-item no-link">
          <b-icon v-if="item.icon.length > 0" size="is-normal" class="icon" :icon="item.icon" />
          <span v-else class="empty-icon-spacer" />
          <span v-if="toggleOpen" class="label">
            {{ item.label }}
          </span>
        </div>
        <!-- Normal links -->
        <div
          v-else-if="item.type == 'NORMAL'"
          @click="
            isDisplayPorteurProjet(item.label)
              ? resetNotif()
              : () => {
                  return false
                }
          "
        >
          <router-link
            class="SubVerticalMenu-item SubVerticalMenu-item-hoverable"
            :to="{ path: item.toPath }"
            :class="{
              'router-link-active': item.isLinkActive,
            }"
            @click="scrollToTop"
          >
            <div
              v-if="allowedMenus.includes(item.label)"
              :class="{
                'menu-item-vertical-bar': item.label !== 'Historique',
                'router-link-desactive': item.isLinkActive,
              }"
            ></div>
            <b-icon
              v-if="item.icon.length > 0"
              size="is-normal"
              class="item-icon"
              :icon="item.icon"
            />
            <span v-else class="empty-icon-spacer"></span>
            <span v-if="toggleOpen" class="item-label label-container">
              {{ item.label }}
              <span v-if="shouldDisplayBadge(item.label)" class="badge">{{
                localPendingRequest.users
              }}</span>
              <span v-if="isDisplayPorteurProjet(item.label)" class="badge porteur decalage">{{
                countModifPorteurGrae
              }}</span>
            </span>
            <div class="SubVerticalMenu-arrow"></div>
          </router-link>

          <!-- Bullet validation sub items -->
          <div
            v-for="(bulletItem, i) in item.children"
            v-show="item.isLinkActive"
            :key="i"
            class="SubVerticalMenu-bullet"
            :class="{ active: activeAnchor == bulletItem.toPath }"
          >
            <a :href="'#' + bulletItem.toPath" @click="onBulletClick(bulletItem.toPath)">
              <div class="bullet-container">
                <div
                  class="bullet"
                  :class="{
                    none: bulletItem.validationStatus == 4 || bulletItem.validationStatus == 5,
                    valid: bulletItem.validationStatus == 0,
                    warning: bulletItem.validationStatus == 1 || bulletItem.validationStatus == 2,
                    error: bulletItem.validationStatus == 3,
                  }"
                ></div>
              </div>
              <span v-if="toggleOpen">
                {{ bulletItem.label }}
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
    <div class="SubVerticalMenu-end">
      <div class="SubVerticalMenu-arrow-button clickable" @click="toggleOpenMenu">
        <b-icon
          size="is-normal"
          :icon="`${toggleOpen ? 'angle-double-left' : 'angle-double-right'}`"
        ></b-icon>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, nextTick } from "vue"
import { t } from "@/utils/i18n-utils"
import { BIcon } from "buefy"
import { NavbarItem } from "@/views/menus/MenuItems"
import { eventBus } from "@/services/EventBus"

export default defineComponent({
  name: "SubVerticalMenu",
  components: {
    BIcon,
  },
  props: {
    items: {
      type: Array as PropType<NavbarItem[]>,
      required: false,
      default: () => [],
    },
    activeAnchor: {
      type: String,
      required: false,
      default: "",
    },
    previousScreenName: {
      type: String,
      required: false,
      default: "",
    },
    menuExpanded: {
      type: Boolean,
      required: false,
    },
    pendingRequestMember: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    pendingRequest: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    idGrae: {
      type: String,
      required: false,
      default: "",
    },
    role: {
      type: String,
      required: false,
      default: "",
    },
    notifications: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },

  emits: ["back-to-previous", "context-menu-expand", "reset-notif-grae"],
  data() {
    return {
      toggleOpen: true,
      allowedMenus: [
        "En bref",
        "Mon année",
        "Infos administratives",
        "Envie de renouveler la labellisation ? ",
        "Infos labellisation",
        "Infos renouvellement",
        "En détails",
        "Dites-nous en plus",
      ],
      labelPorteur: t("menu.ModifProjet"),
      countModifPorteurGrae: 0,
      countUserGrae: 0,
      localPendingRequest: {} as any,
      scrollContainer: null as HTMLElement | null,
      scrollHandler: null as (() => void) | null,
    }
  },
  watch: {
    items: {
      handler(): void {
        this.$nextTick(() => this.setupScrollSpy())
      },
      deep: true,
    },
    menuExpanded(): void {
      this.toggleOpen = this.menuExpanded
    },
    notifications(): void {
      this.setCoutModifPorteurGrae()
    },
    pendingRequest: {
      handler(newVal: any): void {
        this.localPendingRequest = { ...newVal }
        this.checkPendingRequest()
      },
      immediate: true,
      deep: true,
    },
    pendingRequestMember: {
      handler(): void {
        this.checkPendingRequest()
      },
      deep: true,
    },
    $route(): void {
      this.checkPendingRequest()
    },
  },
  mounted(): void {
    this.$nextTick(() => this.setupScrollSpy())
  },
  unmounted(): void {
    this.teardownScrollSpy()
  },
  methods: {
    setCoutModifPorteurGrae(): void {
      if (!this.notifications) return

      const notifs: Array<any> = Object.keys(this.notifications)
      const idGrae = this.$route.params.graeId ? this.$route.params.graeId : this.idGrae

      if (notifs.length > 0 && notifs.includes(idGrae)) {
        Object.entries(this.notifications).forEach((notif) => {
          if (notif[0] == idGrae) {
            this.countModifPorteurGrae = Number(notif[1]) || 0
          }
        })
      }
    },

    toggleOpenMenu(): void {
      this.toggleOpen = !this.toggleOpen
      this.$emit("context-menu-expand", this.toggleOpen)
    },

    checkPendingRequest(): void {
      if (this.role === "ACCOMPAGNATEUR_NATIONAL") {
        const url = new URL(window.location.href)
        const pathParts = url.pathname.split("/")
        const hasMember = pathParts.includes("grae")
        if (hasMember && this.pendingRequestMember) {
          this.localPendingRequest = {
            ...this.localPendingRequest,
            users: this.pendingRequestMember.users,
          }
        }
      }
    },

    shouldDisplayBadge(label: string): boolean {
      if (label === "Membres") {
        if (this.role === "ACCOMPAGNATEUR_NATIONAL" || this.role === "ANIMATEUR_REGIONAL") {
          return this.localPendingRequest.users > 0
        }
      }
      return false
    },
    isDisplayPorteurProjet(label: string): boolean {
      let display = false

      if (this.labelPorteur == label) {
        display = this.countModifPorteurGrae > 0
      }

      return display
    },
    resetNotif(): void {
      const idGrae = this.$route.params.graeId
      this.$emit("reset-notif-grae", { idGrae: idGrae, countNotifGrae: this.countModifPorteurGrae })
      this.countModifPorteurGrae = 0
    },
    onBulletClick(anchor: string): void {
      eventBus.emit("contextual-menu-anchor-update", anchor)
    },
    setupScrollSpy(): void {
      this.teardownScrollSpy()
      const container = document.getElementById("project-content")
      if (!container) return
      this.scrollContainer = container
      this.scrollHandler = () => this.updateActiveAnchorFromScroll()
      container.addEventListener("scroll", this.scrollHandler, { passive: true })
    },
    teardownScrollSpy(): void {
      if (this.scrollContainer && this.scrollHandler) {
        this.scrollContainer.removeEventListener("scroll", this.scrollHandler)
      }
      this.scrollContainer = null
      this.scrollHandler = null
    },
    updateActiveAnchorFromScroll(): void {
      const container = this.scrollContainer
      if (!container) return
      const anchors = this.getActiveBulletAnchors()
      if (anchors.length === 0) return

      const scrollY = container.scrollTop
      const remainingScroll = container.scrollHeight - container.clientHeight - scrollY

      // If near the bottom of the page, select the last anchor
      if (remainingScroll < 100) {
        eventBus.emit("contextual-menu-anchor-update", anchors[anchors.length - 1])
        return
      }

      // Check if the last anchor's element is visible in the viewport
      const lastEl = document.getElementById(anchors[anchors.length - 1])
      if (lastEl) {
        const lastElRect = lastEl.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()
        if (lastElRect.top <= containerRect.top + containerRect.height / 2) {
          eventBus.emit("contextual-menu-anchor-update", anchors[anchors.length - 1])
          return
        }
      }

      for (let i = 0; i < anchors.length; i++) {
        const el = document.getElementById(anchors[i])
        if (!el) continue
        const anchorY = el.offsetTop - container.offsetTop
        if (scrollY <= anchorY - 20) {
          eventBus.emit("contextual-menu-anchor-update", anchors[Math.max(0, i - 1)])
          return
        }
      }
      eventBus.emit("contextual-menu-anchor-update", anchors[anchors.length - 1])
    },
    getActiveBulletAnchors(): string[] {
      const anchors: string[] = []
      for (const item of this.items) {
        if (item.isLinkActive && item.children) {
          for (const child of item.children) {
            if (child.toPath) {
              anchors.push(child.toPath)
            }
          }
        }
      }
      return anchors
    },
    scrollToTop(): void {
      nextTick(() => {
        const projectContent = document.getElementById("project-content")
        if (projectContent) {
          projectContent.scrollTo({ top: 0, behavior: "smooth" })
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" })
        }
      })
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables.scss";

$active-border-width: 4px;

.SubVerticalMenu {
  background-color: $sub-vertical-menu-bg-color;
  width: $main-vertical-menu-width;
  height: 100%;
  box-shadow: 2px 0 0 0 whitesmoke;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .SubVerticalMenu-start {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .SubVerticalMenu-back-to-previous {
    color: $placeholder-default-color;
    cursor: pointer;
    text-transform: uppercase;
    border-bottom: 2px solid black;
    margin-bottom: 5px;
  }

  .SubVerticalMenu-bullet {
    cursor: pointer;
    padding-left: 30px;
    color: $text-light-color;
    height: $main-vertical-menu-width * 0.66;
    padding-top: 10px;
    background-color: $sub-vertical-menu-active-bg-color;

    a {
      color: $text-light-color;
      white-space: nowrap;
      text-overflow: ellipsis;
      display: block;
      max-width: 200px;

      span {
        padding-left: 3px;
      }
    }

    &.active {
      background-color: $ofb-bleu-2;
      color: $main-vertical-menu-bg-color;
      border-left: solid $active-border-width white;
      margin-left: -1 * $active-border-width;

      a {
        color: $main-vertical-menu-bg-color;
      }
    }

    &:hover {
      background-color: $ofb-bleu-2;
      color: $main-vertical-menu-bg-color;
      border-left: solid $active-border-width white;
      margin-left: -1 * $active-border-width;

      a {
        color: $main-vertical-menu-bg-color;
      }
    }
  }

  .SubVerticalMenu-item {
    color: $text-light-color;
    height: $main-vertical-menu-item-height;
    border-bottom: 1px solid #3c3c3c;
    display: flex;
    justify-content: flex-start;
    align-items: center;

    &.no-link {
      display: none;
    }

    .icon {
      color: $ofb-bleu-2;
      margin: 0;
      width: $main-vertical-menu-width;
      height: $main-vertical-menu-width;
    }

    .item-label {
      max-width: 190px;
    }

    &.router-link-active {
      background-color: $sub-vertical-menu-active-bg-color;
      border-left: solid $active-border-width $ofb-bleu-2;

      .icon {
        margin-left: -$active-border-width;
      }

      .SubVerticalMenu-arrow {
        display: inline-block;
        vertical-align: middle;
        width: 0;
        height: 0;
        border-top: 10px solid transparent;
        border-bottom: 10px solid transparent;
        border-right: 10px solid white;
        position: absolute;
        right: 0;
      }
    }
  }

  .SubVerticalMenu-item-hoverable {
    &:hover {
      background-color: $ofb-bleu-2;
      color: $main-vertical-menu-bg-color;

      .icon {
        color: $main-vertical-menu-bg-color;
      }

      &.router-link-active {
        border-left: solid $active-border-width white;
      }
    }
  }

  .SubVerticalMenu-end {
    position: relative;
    flex-direction: column;
  }
}

.SubVerticalMenu-arrow-button {
  display: flex;
  align-items: center;
  justify-content: flex-end;

  .icon {
    width: $main-vertical-menu-width;
    height: $main-vertical-menu-width / 2;
    color: $text-light-color;
    background-color: $ofb-bleu-2;
  }
}

.SubVerticalMenu-feedback {
  margin-bottom: 1.5rem;
  margin-left: calc((#{$main-vertical-menu-width} - #{$feedback-button-width}) / 2);
}

.SubVerticalMenu.open {
  width: $main-main-menu-expand-width;
}

.SubVerticalMenu-arrow {
  display: none;
}

.empty-icon-spacer {
  width: 30px;
}

.menu-item-vertical-bar {
  content: "";
  position: relative;
  left: 13%;
  top: 0;
  height: 43px;
  width: 4px;
  display: inline-block;
  background: linear-gradient(#808080, #808080) no-repeat center/2px 100%;
}

.bullet-container {
  display: inline-block;
  background: linear-gradient(#000, #000) no-repeat center/2px 100%;
  height: 43px;
  padding-top: 10px;
  margin-top: -10px;
}

.item-icon {
  position: relative;
  z-index: 1; /* Higher than the z-index for the bar */
}

.bullet {
  width: 15px;
  height: 15px;
  margin-left: 2px;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid black;

  &.none {
    background-color: $light;
    border-color: $dark;
  }

  &.valid {
    background-color: $success;
    border-color: $ofb-vert-1;
  }

  &.warning {
    background-color: white;
    border-color: $warning;
  }

  &.error {
    background-color: $danger;
    border-color: red;
  }
}

.router-link-desactive {
  left: 11.7% !important;
}

.label-container {
  position: relative;
  display: flex;
  align-items: center;
}

.badge {
  background-color: red;
  color: white;
  border-radius: 50%;
  padding: 0.1em 0.5em;
  font-size: 0.8em;
  margin-left: 0.5em;
  position: absolute;
  top: -10px;
  right: -20px;

  &.porteur {
    right: -40px;
  }
}

.badge .porteur .decalage {
  right: 10px;
  top: 15px;
}
</style>
